import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MapPin, Navigation } from "lucide-react";

const LOCATION_PRESETS = [
  "At office — West Bay Street",
  "At airport (LPIA)",
  "With customer",
  "In maintenance shop",
  "In transit / delivery",
];

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

function timeAgo(iso) {
  if (!iso) return "never";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function LocationCell({ v, onSaved }) {
  const isPreset = LOCATION_PRESETS.includes(v.location_label);
  const [preset, setPreset] = useState(isPreset ? v.location_label : v.location_label ? "custom" : "");
  const [custom, setCustom] = useState(isPreset || !v.location_label ? "" : v.location_label);
  const [note, setNote] = useState(v.location_note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const label = preset === "custom" ? custom.trim() : preset;
    if (!label) return toast.error("Pick or enter a location");
    setSaving(true);
    try {
      await api.patch(`/admin/vehicles/${v.id}/location`, { label, note });
      toast.success(`${v.name} location updated`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableCell data-testid={`location-edit-${v.id}`} className="whitespace-nowrap">
      <div className="flex items-center gap-1.5">
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger data-testid={`location-preset-${v.id}`} className="input-dark h-8 w-48 text-xs"><SelectValue placeholder="Set location…" /></SelectTrigger>
          <SelectContent className="bg-white border-[#E5DED5]">
            {LOCATION_PRESETS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            <SelectItem value="custom">Custom…</SelectItem>
          </SelectContent>
        </Select>
        {preset === "custom" && (
          <Input data-testid={`location-custom-${v.id}`} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. Cable Beach" className="input-dark h-8 w-36 text-xs" />
        )}
        <Input data-testid={`location-note-${v.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="input-dark h-8 w-40 text-xs" />
        <Button data-testid={`location-save-${v.id}`} size="sm" onClick={save} disabled={saving} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-8 px-3 text-xs">
          {saving ? "…" : "Save"}
        </Button>
      </div>
    </TableCell>
  );
}

export default function LocationsTab() {
  const [vehicles, setVehicles] = useState([]);
  const [zones, setZones] = useState([]);
  const [newZone, setNewZone] = useState({ name: "", lat: "", lng: "", radius_m: "2500" });

  const load = () => {
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/admin/geofence-zones").then((r) => setZones(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addZone = async () => {
    const lat = parseFloat(newZone.lat);
    const lng = parseFloat(newZone.lng);
    const radius_m = parseInt(newZone.radius_m, 10) || 2500;
    if (!newZone.name.trim() || isNaN(lat) || isNaN(lng)) return toast.error("Enter a name and valid lat/lng (from Google Maps)");
    try {
      await api.post("/admin/geofence-zones", { name: newZone.name.trim(), lat, lng, radius_m });
      toast.success("Drop-off zone added");
      setNewZone({ name: "", lat: "", lng: "", radius_m: "2500" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeZone = async (z) => {
    if (!window.confirm(`Remove the "${z.name}" drop-off zone?`)) return;
    try {
      await api.delete(`/admin/geofence-zones/${z.id}`);
      toast.success("Zone removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-locations-tab">
      <AdminHeader testid="admin-header-locations" eyebrow="Fleet" title="GPS / Locations" description={<span className="flex items-center gap-2"><Navigation size={15} className="text-[#C71E2E] shrink-0" /> Manual vehicle location log — update where each vehicle is right now. Live GPS tracking can be added later with tracker devices.</span>} />
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Vehicle</TableHead>
              <TableHead>Current Location</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead>Set Location</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.map((v) => (
              <TableRow key={v.id} data-testid={`location-row-${v.id}`} className={ROW}>
                <TableCell className="font-ui font-medium whitespace-nowrap">{v.name}</TableCell>
                <TableCell>
                  {v.location_label ? (
                    <span data-testid={`location-current-${v.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-ui border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 whitespace-nowrap">
                      <MapPin size={11} /> {v.location_label}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs font-ui">—</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs max-w-[180px] truncate">{v.location_note || "—"}</TableCell>
                <TableCell data-testid={`location-updated-${v.id}`} className="text-muted-foreground text-xs whitespace-nowrap">{timeAgo(v.location_updated_at)}</TableCell>
                <LocationCell v={v} onSaved={load} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="mt-10 mb-1 font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Return GPS Geofence Zones</p>
      <p className="mb-4 text-xs font-ui text-[#4A4A4A] max-w-2xl leading-relaxed">
        When a renter submits return photos, their live GPS is checked against these drop-off zones. If the vehicle is outside every zone, both business phones get a text alert with a map link. Add as many zones as you like — office, airport, future drop-off points.
      </p>
      <div data-testid="geofence-zones-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto p-5">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Zone</TableHead>
              <TableHead>Center (lat, lng)</TableHead>
              <TableHead>Radius</TableHead>
              <TableHead>Map</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {zones.length === 0 && (
              <TableRow className={ROW}><TableCell colSpan={5} data-testid="geofence-empty" className="text-muted-foreground font-ui py-8 text-center">No zones yet — add your office and airport below.</TableCell></TableRow>
            )}
            {zones.map((z) => (
              <TableRow key={z.id} data-testid={`geofence-row-${z.id}`} className={ROW}>
                <TableCell className="font-ui font-medium whitespace-nowrap">{z.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{z.lat}, {z.lng}</TableCell>
                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{(z.radius_m / 1609.34).toFixed(1)} mi</TableCell>
                <TableCell>
                  <a data-testid={`geofence-map-${z.id}`} href={`https://www.google.com/maps?q=${z.lat},${z.lng}`} target="_blank" rel="noopener noreferrer" className="text-sm font-ui text-[#C71E2E] underline underline-offset-2">View</a>
                </TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`geofence-delete-${z.id}`} size="sm" variant="outline" onClick={() => removeZone(z)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-red-50 hover:text-red-700 font-ui">Remove</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Input data-testid="geofence-name" value={newZone.name} onChange={(e) => setNewZone({ ...newZone, name: e.target.value })} placeholder="Zone name (e.g. Cable Beach)" className="input-dark h-9 w-52 text-sm" />
          <Input data-testid="geofence-lat" value={newZone.lat} onChange={(e) => setNewZone({ ...newZone, lat: e.target.value })} placeholder="Lat (e.g. 25.0776)" className="input-dark h-9 w-36 text-sm" />
          <Input data-testid="geofence-lng" value={newZone.lng} onChange={(e) => setNewZone({ ...newZone, lng: e.target.value })} placeholder="Lng (e.g. -77.3571)" className="input-dark h-9 w-36 text-sm" />
          <Input data-testid="geofence-radius" value={newZone.radius_m} onChange={(e) => setNewZone({ ...newZone, radius_m: e.target.value })} placeholder="Radius m" className="input-dark h-9 w-28 text-sm" />
          <Button data-testid="geofence-add" size="sm" onClick={addZone} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-9 px-4 text-xs">Add Zone</Button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground font-ui">Tip: find the pin on Google Maps → right-click → the coordinates shown are lat, lng. 2500m ≈ 1.5 miles.</p>
      </div>
    </div>
  );
}
